'use client';

import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useToast} from '@/matic/components/ui/toast';
import {getMasterDataApi} from '@/matic/features/master-data/api/master-data-api';
import {masterDataKeys} from '@/matic/features/master-data/query-keys';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {trackProductEvent} from '@/matic/lib/product-observability';
import type {
  CreateObjectTypeRequest,
  UpdateObjectTypeRequest,
  ObjectType,
  PaginatedResponse,
} from '@/matic/features/master-data/types/master-data-types';

export function useObjectTypeMutations() {
  const createMutation = useCreateObjectType();
  const updateMutation = useUpdateObjectType();
  const archiveMutation = useArchiveObjectType();
  const restoreMutation = useRestoreObjectType();
  const deleteMutation = useDeleteObjectType();

  return {
    createMutation,
    updateMutation,
    archiveMutation,
    restoreMutation,
    deleteMutation,
  };
}

// -- Helpers for prefix-based optimistic updates ----------------------------

type PreviousEntries = Array<
  [readonly unknown[], PaginatedResponse<ObjectType> | undefined]
>;

function optimisticUpdateAllTypeLists(
  queryClient: ReturnType<typeof useQueryClient>,
  updater: (items: ObjectType[]) => ObjectType[]
): PreviousEntries {
  const previous: PreviousEntries = [];
  const entries = queryClient.getQueriesData<PaginatedResponse<ObjectType>>({
    queryKey: masterDataKeys.objectTypeLists(),
  });

  for (const [key, data] of entries) {
    previous.push([key, data]);
    if (data) {
      const updated = updater(data.items);
      queryClient.setQueryData(key, {
        ...data,
        items: updated,
        total: updated.length,
      });
    }
  }

  return previous;
}

function rollbackTypeLists(
  queryClient: ReturnType<typeof useQueryClient>,
  previous: PreviousEntries
) {
  for (const [key, data] of previous) {
    queryClient.setQueryData(key, data);
  }
}

// -- Individual mutation hooks ----------------------------------------------

function useCreateObjectType() {
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const api = getMasterDataApi();

  return useMutation({
    async mutationFn(input: CreateObjectTypeRequest) {
      return api.createObjectType(input);
    },
    onSuccess(created) {
      toast({
        title: 'Category created',
        description: `${created.name} has been created`,
        variant: 'success',
      });
      trackProductEvent('resource.created', {
        resourceType: 'object_type',
        typeId: created.id,
        name: created.name,
      });
    },
    onError(error: Error) {
      toast({
        title: 'Failed to create category',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
    },
  });
}

function useUpdateObjectType() {
  const queryClient = useQueryClient();
  const tenantId = useRequiredTenantId();
  const {toast} = useToast();
  const api = getMasterDataApi();

  return useMutation({
    async mutationFn({
      typeId,
      input,
    }: {
      typeId: string;
      input: UpdateObjectTypeRequest;
    }) {
      return api.updateObjectType(typeId, input);
    },
    onSuccess(updated, variables) {
      toast({
        title: 'Category updated',
        description: `${updated.name} has been updated`,
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_type',
        typeId: updated.id,
        fieldsChanged: Object.keys(variables.input),
      });
    },
    onError(error: Error) {
      toast({
        title: 'Failed to update category',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled(_data, _error, variables) {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectType(variables.typeId, tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
    },
  });
}

function useArchiveObjectType() {
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const api = getMasterDataApi();

  return useMutation({
    async mutationFn(id: string) {
      return api.archiveObjectType(id);
    },
    async onMutate(id: string) {
      await queryClient.cancelQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
      const previous = optimisticUpdateAllTypeLists(queryClient, (items) =>
        items.map((t) =>
          t.id === id ? {...t, deletedAt: new Date().toISOString()} : t
        )
      );
      return {previous};
    },
    onSuccess(archived) {
      toast({
        title: 'Category archived',
        description: `${archived.name} has been archived`,
        variant: 'success',
      });
      if (archived.warnings.length > 0) {
        toast({
          title: 'Warning',
          description: archived.warnings[0],
          variant: 'warning',
        });
      }

      trackProductEvent('resource.updated', {
        resourceType: 'object_type',
        typeId: archived.id,
        archived: true,
      });
    },
    onError(error: Error, _id, context) {
      if (context?.previous) {
        rollbackTypeLists(queryClient, context.previous);
      }

      toast({
        title: 'Failed to archive category',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
    },
  });
}

function useRestoreObjectType() {
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const api = getMasterDataApi();

  return useMutation({
    async mutationFn(id: string) {
      return api.restoreObjectType(id);
    },
    async onMutate(id: string) {
      await queryClient.cancelQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
      const previous = optimisticUpdateAllTypeLists(queryClient, (items) =>
        items.map((t) => (t.id === id ? {...t, deletedAt: null} : t))
      );
      return {previous};
    },
    onSuccess(restored) {
      toast({
        title: 'Category restored',
        description: `${restored.name} is now active`,
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_type',
        typeId: restored.id,
        archived: false,
      });
    },
    onError(error: Error, _id, context) {
      if (context?.previous) {
        rollbackTypeLists(queryClient, context.previous);
      }

      toast({
        title: 'Failed to restore category',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
    },
  });
}

function useDeleteObjectType() {
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const api = getMasterDataApi();

  return useMutation({
    async mutationFn(id: string) {
      return api.deleteObjectType(id);
    },
    async onMutate(id: string) {
      await queryClient.cancelQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
      const previous = optimisticUpdateAllTypeLists(queryClient, (items) =>
        items.filter((t) => t.id !== id)
      );
      return {previous};
    },
    onSuccess(_data, typeId) {
      toast({
        title: 'Category deleted',
        description: 'The category has been permanently removed',
        variant: 'success',
      });
      trackProductEvent('resource.updated', {
        resourceType: 'object_type',
        typeId,
        deleted: true,
      });
    },
    onError(error: Error, _id, context) {
      if (context?.previous) {
        rollbackTypeLists(queryClient, context.previous);
      }

      toast({
        title: 'Failed to delete object type',
        description: error.message,
        variant: 'error',
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectTypeLists(),
      });
    },
  });
}
